import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

/** Plinth the avatar stands on. Square-ish, never a circle — see AVATAR_SIZE. */
export const OPEN_CARD_PLINTH_SIZE = 104;
/**
 * Deliberately smaller than the plinth: our avatars are full-body characters, so they are drawn
 * INSIDE the frame with room to spare rather than filling it. A circular mask (or a frame this
 * art has to fill) clips arms and feet, which is what made the first pass look cropped.
 */
export const OPEN_CARD_AVATAR_SIZE = 92;
export const OPEN_CARD_BADGE_SIZE = 44;

/**
 * Board cards sit on their own purple surfaces, so `gray[200]` (a slate border) reads as a
 * different family. A white hairline is what the design uses to separate them from the shell.
 */
const BOARD_HAIRLINE = 'rgba(255, 255, 255, 0.06)';

/**
 * Web hover lift. A translucent white would composite against the screen behind the card, not the
 * card, so this is the board purple already lightened by that amount.
 */
const BOARD_CARD_HOVER = '#1E1836';

export const getOpenChallengeCardStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      flexDirection: 'row',
      gap: 14,
      backgroundColor: theme.background.boardCard,
      borderRadius: theme.borderRadius.xl,
      borderWidth: 1,
      borderColor: BOARD_HAIRLINE,
      padding: 14,
    },
    /** Kickoff is imminent: the border carries the urgency so the card reads at a glance. */
    cardUrgent: {
      borderColor: theme.error,
    },
    hover: {
      backgroundColor: BOARD_CARD_HOVER,
    },
    pressed: {
      opacity: 0.9,
    },

    // ── hero: the poster ────────────────────────────────────────────────────
    heroColumn: {
      width: OPEN_CARD_PLINTH_SIZE,
      flexShrink: 0,
      alignItems: 'center',
      gap: 10,
    },
    /** Holds the plinth plus the crest and level pill that hang off its corners. */
    plinthWrap: {
      position: 'relative',
    },
    plinth: {
      width: OPEN_CARD_PLINTH_SIZE,
      height: OPEN_CARD_PLINTH_SIZE,
      borderRadius: 22,
      // A ring in the poster's rank colour, per the design; the fill stays the board purple so
      // ten different rank colours never turn the list into a paint chart.
      borderWidth: 3,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.boardPlinth,
      borderColor: BOARD_HAIRLINE,
    },
    rankBadge: {
      position: 'absolute',
      right: -12,
      bottom: -8,
    },
    levelPill: {
      position: 'absolute',
      bottom: -7,
      left: 2,
      borderRadius: theme.borderRadius.full,
      paddingHorizontal: 8,
      paddingVertical: 2,
      // Filled per card with the rank accent.
      backgroundColor: theme.primary,
    },
    levelPillText: {
      fontSize: 9,
      fontWeight: '800',
      color: theme.onPrimary,
    },
    heroIdentity: {
      width: '100%',
      alignItems: 'center',
      gap: 2,
    },
    name: {
      maxWidth: '100%',
      fontSize: 15,
      fontWeight: '800',
      color: theme.black,
      textAlign: 'center',
    },
    tierLabel: {
      fontSize: 8.5,
      fontWeight: '800',
      letterSpacing: 0.5,
      textAlign: 'center',
    },

    // ── detail: the challenge ───────────────────────────────────────────────
    detailColumn: {
      flex: 1,
      minWidth: 0,
      gap: 9,
    },
    entryRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
    },
    entryBlock: {
      flex: 1,
      minWidth: 0,
      gap: 1,
    },
    entryLabel: {
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 0.8,
      color: theme.gray[400],
    },
    entryValueRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
    },
    entryValue: {
      fontSize: 22,
      fontWeight: '800',
      color: theme.accent,
    },
    coinIcon: {
      width: 15,
      height: 15,
    },
    clockPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      flexShrink: 0,
      backgroundColor: theme.background.boardInset,
      borderRadius: theme.borderRadius.full,
      paddingHorizontal: 9,
      paddingVertical: 4,
    },
    clockText: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.gray[500],
    },
    clockTextUrgent: {
      color: theme.error,
    },

    metaPanel: {
      gap: 5,
      backgroundColor: theme.background.boardInset,
      borderRadius: 10,
      paddingHorizontal: 10,
      paddingVertical: 8,
    },
    picksRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
    },
    picksValue: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.gray[700],
    },
    picksLabel: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[500],
    },
    /** Marks a slate bigger than the free pick allowance (see OPEN_BOARD_QUICK_MAX_PICKS). */
    bigSlateCrown: {
      width: 13,
      height: 13,
    },
    league: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[500],
    },
    payoutRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
    },
    payoutLabel: {
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 0.8,
      color: theme.gray[400],
    },
    payoutValue: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.secondary,
    },
    payoutCoin: {
      width: 12,
      height: 12,
    },

    takeBtn: {
      height: theme.touchTargets.minHeight,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.primary,
      borderRadius: 10,
    },
    takeBtnText: {
      fontSize: 14,
      fontWeight: '800',
      color: theme.onPrimary,
    },
  });
